import React, { useEffect, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Plus, Trash, Lock, ArrowUp } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useTheme } from '@mui/material/styles';
import CustomCalendarHeader from '../Calendar/CustomCalendarHeader';
import { useToast } from '../../hooks/useToast';
import { format } from 'date-fns';

const toIsoLocal = (date) => {
  if (!date) return '';
  const pad = (n) => `${n}`.padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const _parseLocal = (value) => {
  const d = new Date(value);
  return isNaN(d) ? null : d;
};

function StaffShiftCalendar({ teamMemberId, _businessId, isFreePlan = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [timeOffEvents, setTimeOffEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({
    start: '',
    end: '',
    repeatPattern: 'none', // 'none', 'weekly', 'biweekly', 'monthly'
    repeatCount: 12,
    breaks: []
  });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [currentView, setCurrentView] = useState('timeGridWeek');
  const [staffMemberName, setStaffMemberName] = useState('Staff Member');

  const loadEvents = async (viewInfo) => {
    if (!teamMemberId) return;
    if (!viewInfo || !viewInfo.startStr || !viewInfo.endStr) {
      return;
    }
    try {
      const start = viewInfo.startStr;
      const end = viewInfo.endStr;
      
      const ts = Date.now();
      // Fetch shifts and schedule separately - schedule might not exist (404), but we still want shifts
      const shiftsRes = await api.get(
        `/api/team-member-shifts/team-member/${teamMemberId}?start=${start}&end=${end}&_=${ts}`,
        { headers: { 'Cache-Control': 'no-cache' } }
      );
      
      let scheduleRes = { data: null };
      try {
        scheduleRes = await api.get(
          `/api/team-member-schedules/team-member/${teamMemberId}`,
          { headers: { 'Cache-Control': 'no-cache' } }
        );
      } catch (scheduleErr) {
        // Schedule doesn't exist (404) - that's OK, we can still show shifts
        if (scheduleErr?.response?.status !== 404) {
          // Re-throw if it's not a 404
          throw scheduleErr;
        }
      }

      // Get user's full name from schedule or fetch staff member directly
      let fullName = 'Staff';
      if (scheduleRes.data?.teamMember) {
        const teamMember = scheduleRes.data.teamMember;
        if (teamMember.user) {
          fullName = `${teamMember.user.firstName || ''} ${teamMember.user.lastName || ''}`.trim() || teamMember.name || 'Staff';
        } else if (teamMember.name) {
          fullName = teamMember.name;
        }
      } else {
        // No schedule yet, try to fetch staff member name directly
        try {
          const staffRes = await api.get(`/api/supplier/staff-members/${teamMemberId}`);
          const staffMember = staffRes.data.staffMember;
          if (staffMember?.user) {
            fullName = `${staffMember.user.firstName || ''} ${staffMember.user.lastName || ''}`.trim() || staffMember.name || 'Staff';
          } else if (staffMember?.name) {
            fullName = staffMember.name;
          }
        } catch (err) {
          // Ignore error, use default 'Staff'
        }
      }
      
      // Store the name for use in the modal
      setStaffMemberName(fullName);

      const shiftEvents = (shiftsRes.data?.shifts || []).map((shift) => ({
        id: shift._id,
        title: `${fullName}'s Shift`,
        start: shift.startTime,
        end: shift.endTime,
        backgroundColor: theme.palette.primary.light,
        borderColor: theme.palette.primary.main,
        extendedProps: {
          type: 'shift',
          breaks: shift.breaks || [],
          seriesId: shift.seriesId
        }
      }));

      const timeOff = (scheduleRes.data?.timeOff || []).map((off) => {
        const reason = off.reason === 'time_off' ? 'Day Off' : (off.reason || 'Day Off');
        return {
          id: off._id,
          title: `${fullName}'s ${reason}`,
          start: off.startDate,
          end: off.endDate,
          backgroundColor: theme.palette.error.light,
          borderColor: theme.palette.error.main,
          display: 'background',
          extendedProps: { type: 'timeOff' }
        };
      });

      // Only update if we got valid data (don't clear on empty results from bad date ranges)
      if (start && end && start !== end) {
        setEvents(shiftEvents);
        setTimeOffEvents(timeOff);
      }
    } catch (err) {
      // Don't clear events on error - keep what we have
    }
  };

  // Load events when teamMemberId changes or calendar mounts
  useEffect(() => {
    if (!teamMemberId) return;
    
    // Wait for calendar to be ready, then load events
    const timer = setTimeout(() => {
      const calendar = calendarRef.current?.getApi();
      if (calendar && calendar.view && calendar.view.activeStart && calendar.view.activeEnd) {
        const viewInfo = {
          startStr: calendar.view.activeStart.toISOString(),
          endStr: calendar.view.activeEnd.toISOString()
        };
        loadEvents(viewInfo);
      } else {
        // If calendar not ready, try again
        const retryTimer = setTimeout(() => {
          const cal = calendarRef.current?.getApi();
          if (cal && cal.view && cal.view.activeStart && cal.view.activeEnd) {
            const viewInfo = {
              startStr: cal.view.activeStart.toISOString(),
              endStr: cal.view.activeEnd.toISOString()
            };
            loadEvents(viewInfo);
          }
        }, 500);
        return () => clearTimeout(retryTimer);
      }
    }, 800);
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [teamMemberId]);


  // Refetch calendar when events state changes (but only if we have events)
  useEffect(() => {
    const calendar = calendarRef.current?.getApi();
    if (calendar && (events.length > 0 || timeOffEvents.length > 0)) {
      // Small delay to ensure state has updated
      const timer = setTimeout(() => {
        calendar.refetchEvents();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [events.length, timeOffEvents.length]);

  // Refetch events after saving a shift
  const _refetchCalendarEvents = () => {
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      calendar.refetchEvents();
    }
  };

  const openCreateDialog = (start, end) => {
    setEditingEvent(null);
    setForm({ start: toIsoLocal(start), end: toIsoLocal(end), repeatPattern: 'none', repeatCount: 12, breaks: [] });
    setDialogOpen(true);
  };

  const handleSelect = (selectionInfo) => {
    openCreateDialog(selectionInfo.start, selectionInfo.end);
  };

  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    if (event.extendedProps.type === 'timeOff') return; // read-only
    setEditingEvent(event);
    setForm({
      start: toIsoLocal(new Date(event.start)),
      end: toIsoLocal(new Date(event.end)),
      repeatPattern: 'none',
      repeatCount: 12,
      breaks: (event.extendedProps.breaks || []).map((b) => ({
        start: toIsoLocal(new Date(b.startTime)),
        end: toIsoLocal(new Date(b.endTime))
      }))
    });
    setDialogOpen(true);
  };

  const addBreak = () => {
    setForm((prev) => ({ ...prev, breaks: [...prev.breaks, { start: form.start, end: form.start }] }));
  };

  const removeBreak = (idx) => {
    setForm((prev) => ({ ...prev, breaks: prev.breaks.filter((_, i) => i !== idx) }));
  };

  const saveShift = async () => {
    if (!form.start || !form.end) {
      showToast('Start and end times are required', 'error');
      return;
    }
    
    if (!teamMemberId) {
      showToast('Team member ID is missing', 'error');
      return;
    }
    
    // Convert datetime-local to ISO string for API
    const startISO = new Date(form.start).toISOString();
    const endISO = new Date(form.end).toISOString();
    
    const payload = {
      teamMemberId,
      startTime: startISO,
      endTime: endISO,
      breaks: form.breaks.map((b) => ({
        startTime: new Date(b.start).toISOString(),
        endTime: new Date(b.end).toISOString()
      })),
      repeatPattern: form.repeatPattern,
      repeatCount: form.repeatCount
    };
    
    try {
      setLoading(true);
      
      if (editingEvent) {
        await api.put(`/api/team-member-shifts/${editingEvent.id}`, {
          startTime: payload.startTime,
          endTime: payload.endTime,
          breaks: payload.breaks
        });
      } else {
        await api.post('/api/team-member-shifts', payload);
      }
      
      // Close dialog first
      setDialogOpen(false);
      setLoading(false);
      
      // Show success toast
      showToast(
        editingEvent ? 'Shift updated successfully' : 'Shift created successfully',
        'success'
      );
      
      // Reset form
      setForm({ start: '', end: '', repeatPattern: 'none', repeatCount: 12, breaks: [] });
      setEditingEvent(null);
      
      // Clear events and reload
      setEvents([]);
      setTimeOffEvents([]);
      
      // Force reload by calling loadEvents with current view
      const calendar = calendarRef.current?.getApi();
      if (calendar) {
        const view = calendar.view;
        setTimeout(() => {
          loadEvents({
            startStr: view.activeStart.toISOString(),
            endStr: view.activeEnd.toISOString()
          });
        }, 100);
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Unable to save shift';
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!editingEvent) return;
    setEventToDelete(editingEvent);
    setDialogOpen(false);
    setDeleteDialogOpen(true);
  };

  const deleteShift = async (deleteSeries = false) => {
    if (!eventToDelete) return;
    try {
      setLoading(true);
      const url = deleteSeries 
        ? `/api/team-member-shifts/${eventToDelete.id}?deleteSeries=true`
        : `/api/team-member-shifts/${eventToDelete.id}`;
      await api.delete(url);
      
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      setLoading(false);
      
      // Show success toast
      showToast(
        deleteSeries ? 'Shift series deleted successfully' : 'Shift deleted successfully',
        'success'
      );
      
      // Clear events and reload
      setEvents([]);
      setTimeOffEvents([]);
      
      // Force reload by calling loadEvents with current view
      const calendar = calendarRef.current?.getApi();
      if (calendar) {
        const view = calendar.view;
        setTimeout(() => {
          loadEvents({
            startStr: view.activeStart.toISOString(),
            endStr: view.activeEnd.toISOString()
          });
        }, 100);
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || 'Unable to delete shift';
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6">Shift calendar</Typography>
          <Typography variant="body2" color="text.secondary">
            Click or drag to add shifts. Use "repeat weekly" for consistent patterns. Time off appears in red.
          </Typography>
          <Box
            sx={{
              '& .fc-button-primary': {
                backgroundColor: '#E91E63 !important',
                borderColor: '#C2185B !important',
                color: '#fff !important',
                '&:hover': {
                  backgroundColor: '#C2185B !important',
                  borderColor: '#AD1457 !important',
                },
                '&:focus': {
                  backgroundColor: '#C2185B !important',
                  borderColor: '#AD1457 !important',
                  boxShadow: '0 0 0 0.2rem rgba(233, 30, 99, 0.25) !important',
                },
              },
              '& .fc-button-active': {
                backgroundColor: '#AD1457 !important',
                borderColor: '#880E4F !important',
                '&:hover': {
                  backgroundColor: '#880E4F !important',
                  borderColor: '#6A0D3A !important',
                },
              },
              '& .fc-button:disabled': {
                backgroundColor: '#F8BBD0 !important',
                borderColor: '#F48FB1 !important',
                color: '#fff !important',
                opacity: 0.6,
              },
              '& .fc-daygrid-day-number, & .fc-col-header-cell-cushion, & .fc-timegrid-slot-label-cushion': {
                color: '#000 !important',
              },
              '& .fc-event-title': {
                color: '#000000 !important',
              },
              '& .fc-event-time': {
                color: '#000000 !important',
              },
              '& .fc-event': {
                '& *': {
                  color: '#000000 !important',
                }
              },
              // White background for calendar
              backgroundColor: '#ffffff',
              '& .fc-scrollgrid': {
                backgroundColor: '#ffffff',
              },
              '& .fc-daygrid-day': {
                backgroundColor: '#ffffff',
              },
              '& .fc-timegrid-col': {
                backgroundColor: '#ffffff',
              },
              '& .fc-timegrid-slot': {
                backgroundColor: '#ffffff',
                // Keep horizontal lines - removed borderTop: none
              },
              '& .fc-timegrid-slot-lane': {
                // Keep horizontal lines - removed borderTop: none
              },
              // Table headers text-align left
              '& .fc-col-header-cell': {
                fontSize: '0.875rem',
                fontWeight: 400,
                padding: '8px 4px',
                textAlign: 'left !important',
              },
              // Day header styling - two-line format
              '& .fc-day-header-content': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                lineHeight: 1.2,
                textAlign: 'left !important',
              },
              '& .fc-day-header-dayname': {
                fontSize: '0.875rem !important',
                fontWeight: '400 !important',
                color: 'rgba(0, 0, 0, 1) !important', // Black for all day names
              },
              '& .fc-day-header-dayname.fc-day-header-today': {
                color: '#4caf50 !important', // Green for today
              },
              '& .fc-day-header-date': {
                fontSize: '0.75rem !important',
                fontWeight: '400 !important',
                color: 'rgba(0, 0, 0, 0.6) !important', // Grey for date
                marginTop: '2px',
              },
            }}
          >
            <CustomCalendarHeader
              calendarRef={calendarRef}
              currentView={currentView}
              onViewChange={(newView) => {
                setCurrentView(newView);
                const calendar = calendarRef.current?.getApi();
                if (calendar) {
                  calendar.changeView(newView);
                }
              }}
              dateRange={dateRange}
              showViewSelector={true}
            />
            <FullCalendar
              key={teamMemberId}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              selectable
              editable={false}
              events={[...events, ...timeOffEvents]}
              select={handleSelect}
              eventClick={handleEventClick}
              datesSet={(info) => {
                if (teamMemberId) {
                  loadEvents(info);
                }
                setDateRange({
                  start: info.start,
                  end: info.end,
                  view: info.view.type
                });
                setCurrentView(info.view.type);
              }}
              firstDay={1}
              headerToolbar={false}
              height="auto"
              eventDisplay="block"
              dayHeaderContent={(dateInfo) => {
                const dayName = format(dateInfo.date, 'EEEE'); // Full day name
                const dateStr = format(dateInfo.date, 'd MMM'); // Date like "29 Dec"
                
                // Check if today
                const today = new Date();
                const isToday = format(dateInfo.date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
                
                // Determine color class - only today gets green, all others black
                let colorClass = 'fc-day-header-weekday';
                if (isToday) {
                  colorClass = 'fc-day-header-today';
                }
                
                return {
                  html: `<div class="fc-day-header-content">
                    <div class="fc-day-header-dayname ${colorClass}">${dayName}</div>
                    <div class="fc-day-header-date">${dateStr}</div>
                  </div>`
                };
              }}
            />
          </Box>
        </Stack>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingEvent ? 'Edit shift' : `Add shift for ${staffMemberName}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start"
                  type="datetime-local"
                  value={form.start}
                  onChange={(e) => setForm({ ...form, start: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End"
                  type="datetime-local"
                  value={form.end}
                  onChange={(e) => setForm({ ...form, end: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>

            {!editingEvent && !isFreePlan && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Repeat pattern</InputLabel>
                  <Select
                    value={form.repeatPattern}
                    label="Repeat pattern"
                    onChange={(e) => setForm({ ...form, repeatPattern: e.target.value })}
                  >
                    <MenuItem value="none">Don't repeat</MenuItem>
                    <MenuItem value="weekly">Every week</MenuItem>
                    <MenuItem value="biweekly">Every other week</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>

                {form.repeatPattern !== 'none' && (
                  <TextField
                    label={form.repeatPattern === 'monthly' ? 'Number of months' : 'Number of occurrences'}
                    type="number"
                    inputProps={{ min: 1, max: form.repeatPattern === 'monthly' ? 12 : 52 }}
                    value={form.repeatCount}
                    onChange={(e) => setForm({ ...form, repeatCount: Number(e.target.value) })}
                    helperText={
                      form.repeatPattern === 'weekly' ? 'How many weeks to repeat' :
                      form.repeatPattern === 'biweekly' ? 'How many occurrences (every 2 weeks)' :
                      'How many months to repeat'
                    }
                  />
                )}
              </>
            )}

            {!editingEvent && isFreePlan && (
              <Alert
                severity="info"
                icon={<Lock size={20} />}
                action={
                  <Button
                    size="small"
                    color="inherit"
                    endIcon={<ArrowUp size={16} />}
                    onClick={() => navigate('/supplier/billing')}
                  >
                    Upgrade
                  </Button>
                }
              >
                Upgrade to use repeating shifts (weekly, biweekly, monthly)
              </Alert>
            )}

            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">Breaks</Typography>
                <Button size="small" startIcon={<Plus size={14} />} onClick={addBreak}>
                  Add break
                </Button>
              </Box>
              {form.breaks.length === 0 && (
                <Typography variant="body2" color="text.secondary">No breaks</Typography>
              )}
              {form.breaks.map((b, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Start"
                    type="datetime-local"
                    value={b.start}
                    onChange={(e) => {
                      const next = [...form.breaks];
                      next[idx] = { ...next[idx], start: e.target.value };
                      setForm({ ...form, breaks: next });
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="End"
                    type="datetime-local"
                    value={b.end}
                    onChange={(e) => {
                      const next = [...form.breaks];
                      next[idx] = { ...next[idx], end: e.target.value };
                      setForm({ ...form, breaks: next });
                    }}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <IconButton onClick={() => removeBreak(idx)} size="small">
                    <Trash size={16} />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          {editingEvent && (
            <Button color="error" onClick={handleDeleteClick} disabled={loading}>
              Delete
            </Button>
          )}
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveShift} disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Shift</DialogTitle>
        <DialogContent>
          <Typography>
            {eventToDelete?.extendedProps?.seriesId 
              ? 'This shift is part of a repeating series. Would you like to delete just this occurrence or the entire series?'
              : 'Are you sure you want to delete this shift?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setEventToDelete(null);
          }}>
            Cancel
          </Button>
          {eventToDelete?.extendedProps?.seriesId && (
            <Button color="error" onClick={() => deleteShift(true)} disabled={loading}>
              Delete Series
            </Button>
          )}
          <Button color="error" variant="contained" onClick={() => deleteShift(false)} disabled={loading}>
            {eventToDelete?.extendedProps?.seriesId ? 'Delete This One Only' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default StaffShiftCalendar;
